/**
 * Phase 3E: Clarification Prompt Generator
 * 
 * Generates neutral, factual clarification prompts for CPTs with
 * INSUFFICIENT or AMBIGUOUS documentation status.
 * 
 * IMPORTANT:
 * - Prompts must be neutral and factual
 * - Must NOT suggest additional procedures
 * - Must NOT generate clinical content
 * - Only references existing CPT requirements
 */

import { CptDetectionResult } from '@/lib/reconciliation/detectionEngine';
import { DocumentationStatusV2, ClarificationPromptType } from './types';

// ============================================================================
// TYPES
// ============================================================================

export interface GeneratedClarificationPrompt {
  cptCode: string;
  requirementId: string;
  requirementLabel: string;
  promptText: string;
  promptType: ClarificationPromptType;
  missingElement: string;
  documentationStatus: DocumentationStatusV2;
}

export interface PromptGenerationResult {
  cptCode: string;
  totalMissingElements: number;
  prompts: GeneratedClarificationPrompt[];
  requiresClarification: boolean;
}

// ============================================================================
// PROMPT TEMPLATES
// ============================================================================

/**
 * Template strings for generating neutral clarification prompts
 * These are factual and do not suggest clinical actions
 */
const PROMPT_TEMPLATES = {
  INSUFFICIENT: (cptCode: string, element: string): string =>
    `For CPT ${cptCode}, payers typically require documentation of "${element}". ` +
    `Please confirm whether this was performed and documented.`,
  
  AMBIGUOUS: (cptCode: string, element: string): string =>
    `For CPT ${cptCode}, the documentation for "${element}" appears incomplete. ` +
    `Please confirm whether this element was performed.`,
  
  NOT_FOUND: (cptCode: string, element: string): string =>
    `CPT ${cptCode} requires documentation of "${element}". ` +
    `No matching documentation was found. Was this element performed?`,
};

// ============================================================================
// PROMPT GENERATION
// ============================================================================

/**
 * Generate clarification prompts for a single CPT based on detection results
 */
export function generatePromptsForCpt(
  cptResult: CptDetectionResult
): PromptGenerationResult {
  const prompts: GeneratedClarificationPrompt[] = [];
  
  // Only generate prompts for INSUFFICIENT or AMBIGUOUS status
  if (
    cptResult.documentationStatus !== 'insufficient' &&
    cptResult.documentationStatus !== 'ambiguous'
  ) {
    return {
      cptCode: cptResult.cptCode,
      totalMissingElements: 0,
      prompts: [],
      requiresClarification: false,
    };
  }
  
  // Find missing required elements
  const missingElements = cptResult.elementResults.filter(
    el => el.strictness === 'required' && !el.matched
  );
  
  // Generate a prompt for each missing element
  for (const element of missingElements) {
    const templateFn = cptResult.documentationStatus === 'insufficient'
      ? PROMPT_TEMPLATES.INSUFFICIENT
      : PROMPT_TEMPLATES.AMBIGUOUS;
    
    const promptText = templateFn(cptResult.cptCode, element.elementLabel);
    
    prompts.push({
      cptCode: cptResult.cptCode,
      requirementId: element.elementId,
      requirementLabel: element.elementLabel,
      promptText,
      promptType: 'YES_NO',
      missingElement: element.elementLabel,
      documentationStatus: cptResult.documentationStatus,
    });
  }
  
  return {
    cptCode: cptResult.cptCode,
    totalMissingElements: missingElements.length,
    prompts,
    requiresClarification: prompts.length > 0,
  };
}

/**
 * Generate clarification prompts for all CPTs that need them
 */
export function generateAllClarificationPrompts(
  cptResults: CptDetectionResult[]
): {
  totalPromptsGenerated: number;
  cptsRequiringClarification: number;
  allPrompts: GeneratedClarificationPrompt[];
  promptsByCpt: Record<string, GeneratedClarificationPrompt[]>;
} {
  const allPrompts: GeneratedClarificationPrompt[] = [];
  const promptsByCpt: Record<string, GeneratedClarificationPrompt[]> = {};
  let cptsRequiringClarification = 0;
  
  for (const cptResult of cptResults) {
    const result = generatePromptsForCpt(cptResult);
    
    if (result.requiresClarification) {
      cptsRequiringClarification++;
      allPrompts.push(...result.prompts);
      promptsByCpt[cptResult.cptCode] = result.prompts;
    }
  }
  
  return {
    totalPromptsGenerated: allPrompts.length,
    cptsRequiringClarification,
    allPrompts,
    promptsByCpt,
  };
}

// ============================================================================
// SURGEON CONFIRMATION PROMPT
// ============================================================================

/**
 * Generate the final CPT-level confirmation prompt
 * This is required after all clarifications are resolved
 */
export function generateConfirmationPrompt(cptCode: string): string {
  return `Confirm CPT ${cptCode} was performed as documented.`;
}

/**
 * Response labels for surgeon confirmation
 */
export const SURGEON_CONFIRMATION_OPTIONS = [
  { value: 'confirmed_performed', label: 'Confirmed Performed', description: 'Procedure was performed as documented' },
  { value: 'not_performed', label: 'Not Performed', description: 'Procedure was not performed' },
  { value: 'not_applicable', label: 'Not Applicable', description: 'This CPT does not apply to this case' },
  { value: 'deferred', label: 'Deferred', description: 'Decision deferred for later review' },
] as const;

/**
 * Check if a CPT can be marked as billable based on all conditions
 */
export function canMarkBillable(
  isAuthorized: boolean,
  documentationStatus: DocumentationStatusV2,
  surgeonConfirmation: 'confirmed_performed' | 'not_performed' | 'not_applicable' | 'deferred' | null,
  allClarificationsResolved: boolean
): { canBill: boolean; reason: string } {
  // Rule 1: Must be authorized
  if (!isAuthorized) {
    return { canBill: false, reason: 'CPT was not part of the authorized bundle' };
  }
  
  // Rule 2: Documentation must be sufficient (or clarifications resolved to make it sufficient)
  if (documentationStatus !== 'sufficient') {
    if (!allClarificationsResolved) {
      return { canBill: false, reason: 'Pending clarification responses' };
    }
    // Even if clarifications resolved, if they resulted in insufficient docs, not billable
    return { canBill: false, reason: 'Documentation remains insufficient after clarification' };
  }
  
  // Rule 3: Surgeon confirmation is required
  if (!surgeonConfirmation) {
    return { canBill: false, reason: 'Surgeon confirmation not provided' };
  }
  
  // Rule 4: Only confirmed_performed is billable
  if (surgeonConfirmation === 'confirmed_performed') {
    return { canBill: true, reason: 'Surgeon confirmed performed with sufficient documentation' };
  }
  
  if (surgeonConfirmation === 'not_performed') {
    return { canBill: false, reason: 'Surgeon confirmed procedure was not performed' };
  }
  
  if (surgeonConfirmation === 'not_applicable') {
    return { canBill: false, reason: 'Surgeon marked as not applicable' };
  }
  
  if (surgeonConfirmation === 'deferred') {
    return { canBill: false, reason: 'Confirmation deferred by surgeon' };
  }
  
  return { canBill: false, reason: 'Unknown status' };
}
